import { apiError } from '../apiError';
import api from '../axios.config';
import { ApiResponse } from 'types';

/**
 * External telephony database connections, per organization.
 *
 * Call records for some tenants live on the SIP servers that handled the call
 * rather than in cpass; these are the connections the Telephony pages read
 * through. The password is write-only — the API never returns it, and sending
 * a blank one on update means "leave unchanged".
 */
export interface DataSource {
  id?: string;
  name: string;
  sourceKind: 'LANDLINE' | 'MOBILE' | 'OTHER';
  host: string;
  port: number;
  databaseName: string;
  username: string;
  password?: string;
  /** Comma-separated SQL LIKE patterns matched against the source's business_id. */
  businessIdPatterns?: string;
  enabled?: boolean;
  queryTimeoutSeconds?: number;
  hasPassword?: boolean;
  lastTestedAt?: string;
  lastTestOk?: boolean;
  lastTestMessage?: string;
}

export interface TestResult { ok: boolean; elapsedMs?: number; message: string; hint?: string | null; }
export interface DiscoveredBusiness { businessId: string; calls: number; newest: string; }
export interface PatternPreview { matched: number; businessIds: { businessId: string; calls: number }[]; }

const BASE = '/v1/telephony/data-sources';
// Shared so a 403 reads as a refusal rather than a load failure.
const err = (e: any, msg: string) => apiError(e, msg);

export const dataSourcesService = {
  list: async (): Promise<ApiResponse<DataSource[]>> => {
    try { return (await api.get(BASE)).data; } catch (e: any) { return err(e, 'Failed to load data sources'); }
  },
  create: async (body: DataSource): Promise<ApiResponse<DataSource>> => {
    try { return (await api.post(BASE, body)).data; } catch (e: any) { return err(e, 'Failed to create data source'); }
  },
  update: async (id: string, body: DataSource): Promise<ApiResponse<DataSource>> => {
    try { return (await api.put(`${BASE}/${id}`, body)).data; } catch (e: any) { return err(e, 'Failed to update data source'); }
  },
  remove: async (id: string): Promise<ApiResponse<void>> => {
    try { return (await api.delete(`${BASE}/${id}`)).data; } catch (e: any) { return err(e, 'Failed to delete data source'); }
  },
  test: async (id: string): Promise<ApiResponse<TestResult>> => {
    try { return (await api.post(`${BASE}/${id}/test`)).data; } catch (e: any) { return err(e, 'Connection test failed'); }
  },
  businesses: async (id: string, limit = 50): Promise<ApiResponse<DiscoveredBusiness[]>> => {
    try { return (await api.get(`${BASE}/${id}/businesses`, { params: { limit } })).data; }
    catch (e: any) { return err(e, 'Failed to read businesses from source'); }
  },
  patternPreview: async (id: string): Promise<ApiResponse<PatternPreview>> => {
    try { return (await api.get(`${BASE}/${id}/pattern-preview`)).data; }
    catch (e: any) { return err(e, 'Failed to preview patterns'); }
  },
};
